import { FC, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '@hooks/useAuth';
import { propertyService } from '@services/property.service';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const navItems = [
  { path: '/dashboard', label: 'Tableau de bord', icon: '🏠' },
  { path: '/first-selection', label: '1ère sélection', icon: '✅' },
  { path: '/opportunities', label: 'Opportunités', icon: '⭐' },
  { path: '/portfolio', label: 'Portefeuille', icon: '📊' },
  { path: '/comparateur', label: 'Comparateur', icon: '⚖️' },
];

const profileItem = { path: '/profile', label: 'Mon profil', icon: '👤' };

export const Sidebar: FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout, hasMinRole } = useAuth();
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    propertyService.getCounts().then(setCounts).catch(() => {});
  }, [location.pathname]);

  const countFor = (path: string): number | null => {
    if (path === '/first-selection') return counts['FIRST_SELECTION'] ?? null;
    if (path === '/opportunities') return counts['OPPORTUNITY'] ?? null;
    return null;
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed top-0 left-0 z-30 h-full w-64 bg-primary-600 dark:bg-primary-800',
          'transition-transform duration-300 ease-in-out',
          'lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-primary-500/30">
            <img
              src="/assets/logoImmoHelvet.png"
              alt="ImmoHelvet"
              className="h-8 sm:h-10"
            />
            <button
              onClick={onClose}
              aria-label="Fermer le menu de navigation"
              className="lg:hidden text-white/80 hover:text-white p-1"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Zone scrollable : nav + paramètres */}
          <div className="flex-1 overflow-y-auto">

          {/* Navigation principale */}
          <nav className="px-4 py-6 space-y-2">
            {navItems.map((item) => {
              const count = countFor(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={clsx(
                    'flex items-center px-4 py-3 rounded-lg transition-colors',
                    isActive(item.path)
                      ? 'bg-accent-500 text-white'
                      : 'text-white/80 hover:bg-primary-500/50 hover:text-white'
                  )}
                >
                  <span className="text-xl mr-3">{item.icon}</span>
                  <span className="font-medium">{item.label}</span>
                  {count != null && count > 0 && (
                    <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-white/20 text-white text-xs font-semibold flex items-center justify-center">
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Paramètres et Profil */}
          <div className="px-4 py-3 border-t border-primary-500/30 space-y-2 pb-2">
            {hasMinRole('ADMIN') && (
              <Link
                to="/admin"
                onClick={onClose}
                className={clsx(
                  'flex items-center px-4 py-3 rounded-lg transition-colors',
                  isActive('/admin')
                    ? 'bg-accent-500 text-white'
                    : 'text-white/80 hover:bg-primary-500/50 hover:text-white'
                )}
              >
                <span className="text-xl mr-3">🛡️</span>
                <span className="font-medium">Administration</span>
              </Link>
            )}
            <Link
              to={profileItem.path}
              onClick={onClose}
              className={clsx(
                'flex items-center px-4 py-3 rounded-lg transition-colors',
                isActive(profileItem.path)
                  ? 'bg-accent-500 text-white'
                  : 'text-white/80 hover:bg-primary-500/50 hover:text-white'
              )}
            >
              <span className="text-xl mr-3">{profileItem.icon}</span>
              <span className="font-medium">{profileItem.label}</span>
            </Link>
            <Link
              to="/faq"
              onClick={onClose}
              className="flex items-center px-4 py-3 rounded-lg transition-colors text-white/80 hover:bg-primary-500/50 hover:text-white"
            >
              <span className="text-xl mr-3">❓</span>
              <span className="font-medium">FAQ / Aide</span>
            </Link>
          </div>
          </div>{/* fin zone scrollable */}

          {/* User info */}
          <div className="px-4 py-4 border-t border-primary-500/30">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center text-white font-semibold">
                {user?.firstName?.charAt(0).toUpperCase() || user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user?.name || 'Utilisateur'}
                </p>
                <p className="text-xs text-white/60 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white/80 hover:text-white hover:bg-red-500/20 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Déconnexion
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};
